// Turns a failed auth attempt into something a user can act on and a tester can report.
//
// Written after a closed-testing report of "Google sign-in failed" on Android that cost a
// full debugging cycle. Both call sites used `err.response?.data?.error || '<generic>'`,
// which collapses three very different failures into one indistinguishable string:
//
//   1. the server answered and rejected us      → its message is the useful thing
//   2. the request never completed at all       → nothing is wrong with sign-in
//   3. something threw before any request       → the thrown message is the useful thing
//
// Case 2 is the one that misleads. `api.agence.finance` sits behind Cloudflare, so a
// WAF/country/bot rule, a captive portal, or a device that is simply offline all produce a
// request that never reaches the server — no server-side log exists to check afterwards,
// and the user is told "sign-in failed" when sign-in worked perfectly.
//
// Naming the host in that message is deliberate: it makes a blocked network diagnosable
// from a screenshot alone, without shipping a new build to find out.

const API_HOST = 'api.agence.finance';

export function authErrorMessage(err, fallback) {
  // The server answered. Its own text beats anything we could synthesize.
  const serverMessage = err?.response?.data?.error;
  if (serverMessage) return serverMessage;

  // A request object with no response means it went out and nothing came back.
  // `err.response` is absent on exactly this class of axios failure.
  if (err?.request && !err?.response) {
    const code = err.code ? ` (${err.code})` : '';
    return `Could not reach ${API_HOST}${code}. Sign-in itself may have worked — this is a network problem. `
      + 'Try mobile data instead of Wi-Fi, or another network.';
  }

  // Anything else: a native plugin rejection, a thrown Error, a bug. The plugin layer
  // guarantees a non-empty message, so this is real diagnostic text when present.
  return (typeof err?.message === 'string' && err.message) ? err.message : fallback;
}
